/**
 * Banner shown to logged-in users who hold a grandfathered v1
 * subscription. Confirms their existing price is locked in and
 * directs them to billing if they want to see plan details. Hidden
 * for users who are NOT subscribed and for users already on v2 SKUs.
 *
 * Why a separate component: this banner appears on Pricing.jsx (and
 * could later appear on AccountBilling), and the grandfather rules
 * are stable enough to live in one place instead of duplicating the
 * SKU-detection logic in two surfaces.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ShieldCheckIcon as ShieldCheck } from "@phosphor-icons/react";

// Legacy v1 plan SKUs. If the user's active subscription package_id is
// one of these, we show the lock-in banner. Keep in sync with
// `backend/routers/billing/packages.py::LEGACY_ONLY_PACKAGES`.
const LEGACY_SKUS = new Set([
  "basic_plan", "basic_plan_annual",
  "pro_plan", "pro_plan_annual",
  "kennel_elite", "kennel_elite_annual",
]);

const PRICE_BY_SKU = {
  basic_plan: "$14.99/mo",
  basic_plan_annual: "$129/yr",
  pro_plan: "$29.99/mo",
  pro_plan_annual: "$249/yr",
  kennel_elite: "$99/mo",
  kennel_elite_annual: "$899/yr",
};

const LABEL_BY_SKU = {
  basic_plan: "Breeder Basic",
  basic_plan_annual: "Breeder Basic (annual)",
  pro_plan: "Breeder Pro",
  pro_plan_annual: "Breeder Pro (annual)",
  kennel_elite: "Kennel Elite",
  kennel_elite_annual: "Kennel Elite (annual)",
};

export default function GrandfatherBanner() {
  const { user } = useAuth();
  const [legacySub, setLegacySub] = useState(null);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    api
      .get("/billing/my-subscription")
      .then((r) => {
        // Endpoint may return null, an object, or an array — pick
        // the most recent active legacy sub if one exists.
        const data = r.data;
        const subs = Array.isArray(data) ? data : data ? [data] : [];
        const legacy = subs.find(
          (s) => s && LEGACY_SKUS.has(s.package_id) && s.expires_at,
        );
        if (!cancelled) setLegacySub(legacy || null);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [user]);

  if (!legacySub) return null;

  const price = PRICE_BY_SKU[legacySub.package_id] || "your current rate";
  const label = LABEL_BY_SKU[legacySub.package_id] || "your current plan";

  return (
    <div
      className="mt-6 rounded-2xl border border-gold/40 bg-gold/5 p-5 md:p-6 flex items-start gap-4"
      data-testid="grandfather-banner"
    >
      <ShieldCheck
        size={28}
        weight="duotone"
        className="text-gold flex-shrink-0 mt-0.5"
      />
      <div className="flex-1">
        <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#8A6A00] mb-1">
          Your price is locked in
        </p>
        <p className="text-sm text-ink leading-relaxed">
          You're on <strong>{label}</strong> at <strong>{price}</strong>. As
          a founding subscriber, this rate stays yours for as long as you
          remain subscribed — even as we update prices for new breeders.
          No surprises, no forced upgrades.
        </p>
        <Link
          to="/account/billing"
          className="inline-block mt-2 text-[12px] text-ink underline hover:text-gold"
          data-testid="grandfather-banner-billing-link"
        >
          See your plan details →
        </Link>
      </div>
    </div>
  );
}
